import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { listEligibleExpiryGrLines, listExpiryLots, receiveExpiryLot, type EligibleExpiryGrLine } from '../../api/inventoryOps';
import { listItems } from '../../api/masterData';
import { listWarehouses } from '../../api/orgData';
import { usePermissions } from '../../context/PermissionContext';
import { useBranch } from '../../context/BranchContext';
import {
  Button,
  ClickableRow,
  Drawer,
  DrawerField,
  EmptyState,
  ErrorNote,
  FieldLabel,
  ListToolbar,
  LoadingNote,
  PageHeader,
  Pagination,
  Select,
  StatusBadge,
  SummaryStrip,
  TextInput,
  statusCounts,
  tableStyle,
  tdStyle,
  thStyle,
  usePagination,
} from '../../components/common';
import type { ExpiryLot, ExpiryLotStatus, Item, Warehouse } from '../../types/domain';

const LOT_STATUSES: ExpiryLotStatus[] = ['ACTIVE', 'DEPLETED', 'EXPIRED'];

/**
 * Expiry / Lot Tracking — real receiving flow (fn_receive_expiry_lot_checked,
 * migration 0033/0034). The GR-line picker is backed by
 * fn_list_eligible_expiry_gr_lines (migration 0034), which only surfaces
 * POSTED goods-receipt lines for tracks_expiry items that still have
 * unallocated quantity — an already-fully-allocated line, a non-tracked
 * item, or a DRAFT GR simply never appears, enforced server-side, not by
 * frontend filtering. Every write goes through the permission-gated RPC,
 * same pattern as every other FASE 6/VS1 mutation.
 */
export default function ExpiryLotsPage() {
  const { hasPermission } = usePermissions();
  const { activeBranchId } = useBranch();
  const [lots, setLots] = useState<ExpiryLot[]>([]);
  const [eligibleLines, setEligibleLines] = useState<EligibleExpiryGrLine[]>([]);
  const [itemsById, setItemsById] = useState<Record<string, Item>>({});
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedLot, setSelectedLot] = useState<ExpiryLot | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const canCreate = hasPermission('expiry_lot', 'create');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [lotRows, itemRows, whRows, eligibleRows] = await Promise.all([
        listExpiryLots(activeBranchId ?? undefined),
        listItems(),
        listWarehouses(),
        canCreate ? listEligibleExpiryGrLines() : Promise.resolve([]),
      ]);
      setLots(lotRows);
      setItemsById(Object.fromEntries(itemRows.map((it) => [it.item_id, it])));
      setWarehouses(whRows);
      setEligibleLines(eligibleRows);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  // expiry_lots is branch-scoped — re-fetch on branch change.
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranchId]);

  const warehouseName = (id: string) => warehouses.find((w) => w.warehouse_id === id)?.name ?? id;
  const daysLeftOf = (l: ExpiryLot) => Math.ceil((new Date(l.expiry_date).getTime() - Date.now()) / 86400000);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return lots.filter((l) => {
      if (statusFilter !== 'ALL' && l.status !== statusFilter) return false;
      if (q) {
        const code = itemsById[l.item_id]?.item_code?.toLowerCase() ?? '';
        const name = itemsById[l.item_id]?.name?.toLowerCase() ?? '';
        if (!code.includes(q) && !name.includes(q) && !l.lot_no.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [lots, search, statusFilter, itemsById]);
  const { pageRows, page, pageCount, setPage, start, pageSize, total } = usePagination(filtered, 15);
  const expiringSoonCount = lots.filter((l) => l.status === 'ACTIVE' && daysLeftOf(l) <= 7).length;
  const lotCounts = statusCounts(lots, LOT_STATUSES);

  return (
    <div>
      <PageHeader title="Expiry / Lot Tracking" subtitle="Pelacakan lot & tanggal kadaluarsa — migration 0012/0033/0034." />
      {canCreate && (
        <Button variant="primary" onClick={() => setShowForm((v) => !v)} style={{ marginBottom: 16 }} disabled={eligibleLines.length === 0}>
          {showForm ? 'Batal' : '+ Terima lot'}
        </Button>
      )}
      {canCreate && !loading && eligibleLines.length === 0 && (
        <ErrorNote text="Tidak ada baris Goods Receipt POSTED untuk item tracks_expiry yang masih memiliki sisa alokasi." />
      )}
      {showForm && canCreate && (
        <ReceiveLotForm
          eligibleLines={eligibleLines}
          itemsById={itemsById}
          onReceived={() => {
            setShowForm(false);
            void load();
          }}
        />
      )}
      {loading && <LoadingNote />}
      {error && <ErrorNote text={error} />}
      {!loading && !error && lots.length > 0 && (
        <>
          {/* First 4 chips are the exhaustive, mutually-exclusive status
              breakdown (Active + Depleted + Expired always sums to Total).
              "Akan Kadaluarsa" is a separate, overlapping exception
              highlight — a subset of Active — kept distinct so it never
              looks like a status the Total omitted. */}
          <SummaryStrip
            items={[
              { label: 'Total Lot', value: lots.length },
              { label: 'Active', value: lotCounts.ACTIVE, tone: 'success' },
              { label: 'Depleted', value: lotCounts.DEPLETED, tone: 'neutral' },
              { label: 'Expired', value: lotCounts.EXPIRED, tone: lotCounts.EXPIRED > 0 ? 'danger' : 'success' },
              { label: 'Akan Kadaluarsa (≤7 hari, dari Active)', value: expiringSoonCount, tone: expiringSoonCount > 0 ? 'warning' : 'success' },
            ]}
          />
          <ListToolbar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Cari item atau no. lot..."
            resultCount={total}
            filters={
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: 160 }}>
                <option value="ALL">Semua status</option>
                <option value="ACTIVE">Active</option>
                <option value="DEPLETED">Depleted</option>
                <option value="EXPIRED">Expired</option>
              </Select>
            }
          />
        </>
      )}
      {!loading && !error && lots.length === 0 && <EmptyState text="Belum ada lot tercatat." />}
      {!loading && !error && lots.length > 0 && total === 0 && <EmptyState text="Tidak ada lot yang cocok dengan pencarian/filter." />}
      {total > 0 && (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Item</th>
              <th style={thStyle}>No. Lot</th>
              <th style={thStyle}>Kadaluarsa</th>
              <th style={thStyle}>Sisa hari</th>
              <th style={thStyle}>Sisa / Diterima</th>
              <th style={thStyle}>Status</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((l) => {
              const daysLeft = daysLeftOf(l);
              return (
                <ClickableRow key={l.lot_id} onClick={() => setSelectedLot(l)}>
                  <td style={tdStyle}>{itemsById[l.item_id]?.item_code ?? l.item_id}</td>
                  <td style={{ ...tdStyle, color: 'var(--alex-primary)', fontWeight: 600 }}>{l.lot_no}</td>
                  <td style={tdStyle}>{l.expiry_date}</td>
                  <td style={{ ...tdStyle, color: daysLeft <= 7 ? 'var(--alex-danger)' : daysLeft <= 30 ? '#92620A' : 'var(--alex-text-secondary)', fontWeight: 600 }}>
                    {daysLeft >= 0 ? `${daysLeft} hari` : 'Kadaluarsa'}
                  </td>
                  <td style={tdStyle}>
                    {l.qty_remaining} / {l.qty_received}
                  </td>
                  <td style={tdStyle}>
                    <StatusBadge status={l.status} />
                  </td>
                </ClickableRow>
              );
            })}
          </tbody>
        </table>
      )}
      <Pagination page={page} pageCount={pageCount} setPage={setPage} start={start} pageSize={pageSize} total={total} />

      <Drawer open={!!selectedLot} onClose={() => setSelectedLot(null)} title={selectedLot ? `Lot ${selectedLot.lot_no}` : 'Detail Lot'}>
        {selectedLot && (
          <div>
            <DrawerField label="No. Lot" value={selectedLot.lot_no} />
            <DrawerField label="Item" value={itemsById[selectedLot.item_id]?.name ?? selectedLot.item_id} />
            <DrawerField label="Status" value={<StatusBadge status={selectedLot.status} />} />
            <DrawerField label="Tanggal kadaluarsa" value={selectedLot.expiry_date} />
            <DrawerField label="Qty diterima" value={selectedLot.qty_received} />
            <DrawerField label="Qty tersisa" value={selectedLot.qty_remaining} />
            <DrawerField label="Gudang" value={warehouseName(selectedLot.warehouse_id)} />
          </div>
        )}
      </Drawer>
    </div>
  );
}

function ReceiveLotForm({
  eligibleLines,
  itemsById,
  onReceived,
}: {
  eligibleLines: EligibleExpiryGrLine[];
  itemsById: Record<string, Item>;
  onReceived: () => void;
}) {
  const [grLineId, setGrLineId] = useState('');
  const [lotNo, setLotNo] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [qty, setQty] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const selectedLine = eligibleLines.find((l) => l.gr_line_id === grLineId);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(false);
    try {
      await receiveExpiryLot(grLineId, lotNo, expiryDate, Number(qty));
      setSuccess(true);
      setTimeout(onReceived, 600);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="alex-card" style={{ marginBottom: 20, padding: 16 }}>
      <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
        <div style={{ flex: 2 }}>
          <FieldLabel label="Baris Goods Receipt (POSTED, sisa alokasi > 0)">
            <Select value={grLineId} onChange={(e) => setGrLineId(e.target.value)} required>
              <option value="" disabled>
                Pilih...
              </option>
              {eligibleLines.map((l) => (
                <option key={l.gr_line_id} value={l.gr_line_id}>
                  {l.gr_number} — {itemsById[l.item_id]?.item_code ?? l.item_id} (sisa {l.qty_remaining} dari {l.qty_received})
                </option>
              ))}
            </Select>
          </FieldLabel>
        </div>
      </div>
      {selectedLine && (
        <div
          style={{
            fontSize: 'var(--alex-fs-caption)',
            color: 'var(--alex-text-secondary)',
            background: 'var(--alex-surface-subtle)',
            borderRadius: 'var(--alex-control-radius)',
            padding: '8px 12px',
            marginBottom: 12,
          }}
        >
          Item: <b>{itemsById[selectedLine.item_id]?.name ?? selectedLine.item_id}</b> · Sisa yang bisa dialokasikan:{' '}
          <b>{selectedLine.qty_remaining}</b> (dari total diterima {selectedLine.qty_received}, sudah dialokasikan {selectedLine.qty_allocated})
        </div>
      )}
      <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ width: 180 }}>
          <FieldLabel label="No. Lot / Batch">
            <TextInput value={lotNo} onChange={(e) => setLotNo(e.target.value)} required />
          </FieldLabel>
        </div>
        <div style={{ width: 180 }}>
          <FieldLabel label="Tanggal kadaluarsa">
            <TextInput type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} required />
          </FieldLabel>
        </div>
        <div style={{ width: 140 }}>
          <FieldLabel label="Qty diterima">
            <TextInput
              type="number"
              min="0.01"
              step="0.01"
              max={selectedLine?.qty_remaining}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              required
            />
          </FieldLabel>
        </div>
      </div>
      <Button type="submit" variant="primary" loading={submitting} disabled={!grLineId}>
        Terima Lot
      </Button>
      {success && <p style={{ color: 'var(--alex-success)', fontSize: 'var(--alex-fs-caption)', marginTop: 8 }}>Lot berhasil diterima.</p>}
      {error && <ErrorNote text={error} />}
    </form>
  );
}
